import React, { useState, useEffect } from "react";
import { collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LabTestSelector from "./LabTestSelector"; // Import LabTestSelector component
import { useSearchParams } from "react-router-dom";

const LabTestMultiSelector = () => {
  const [searchParams] = useSearchParams();
  const docId = searchParams.get("id");
  const [selectedTests, setSelectedTests] = useState([]);
  const [isExistingDoc, setIsExistingDoc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Snackbar state for success/error messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Fetch existing data from Firestore or allow for new entry
  const fetchData = async () => {
    if (!docId) {
      setError("No document ID provided in the URL.");
      setLoading(false);
      return;
    }

    try {
      const docRef = doc(collection(db, "clinic"), docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setSelectedTests(data.labtest || []);
        setIsExistingDoc(true);
      } else {
        setIsExistingDoc(false);
      }
    } catch (err) {
      console.error("Error fetching document:", err);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [docId]);

  const handleLabTestSelect = (labTest) => {
    setSelectedTests((prev) => {
      const isAlreadySelected = prev.some((test) => test.id === labTest.id);
      if (isAlreadySelected) {
        return prev;
      }
      return [
        ...prev,
        {
          id: labTest.id,
          test_name: labTest.name || "",
          note: "",
          status: "Active",
        },
      ];
    });
  };

  const handleTestUpdate = (index, field, value) => {
    setSelectedTests((prev) =>
      prev.map((test, i) =>
        i === index ? { ...test, [field]: value } : test
      )
    );
  };

  const handleRemoveTest = (testId) => {
    setSelectedTests((prev) =>
      prev.filter((test) => test.id !== testId)
    );
  };

  const handleSubmit = async () => {
    if (!docId) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Invalid document ID. Cannot save data.");
      setSnackbarOpen(true);
      return;
    }

    try {
      const payload = {
        labtest: selectedTests,
      };

      const docRef = doc(collection(db, "clinic"), docId);

      if (isExistingDoc) {
        await updateDoc(docRef, payload);
      } else {
        await setDoc(docRef, payload);
      }

      setSnackbarSeverity("success");
      setSnackbarMessage(
        `Data ${isExistingDoc ? "updated" : "created"} successfully for Document ID: ${docId}`
      );
      setSnackbarOpen(true);

      fetchData(); // Re-fetch data after save
    } catch (error) {
      console.error("Error saving data:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to save data. Please try again.");
      setSnackbarOpen(true);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography  sx={{marginBlock:'20px'}}>
        {isExistingDoc ? "UPDATE LAB TEST" : "ADD LAB TEST"}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
         
          {selectedTests.length > 0 ? (
            selectedTests.map((test, index) => (
              <Box
                key={test.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                
                <Typography sx={{ flex: 1 }}>{test.test_name}</Typography>
                <TextField
                  label="Note"
                  variant="outlined"
                  size="small"
                  value={test.note}
                  onChange={(e) =>
                    handleTestUpdate(index, "note", e.target.value)
                  }
                  sx={{ flex: 2 }}
                />
                <Select
                  value={test.status}
                  onChange={(e) =>
                    handleTestUpdate(index, "status", e.target.value)
                  }
                  size="small"
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </Select>
                <IconButton
                  onClick={() => handleRemoveTest(test.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))
          ) : (
            <Typography>No tests selected yet.</Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <LabTestSelector onSelectLabTest={handleLabTestSelect} />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!selectedTests.length}
          >
            {isExistingDoc ? "Update Document" : "Create Document"}
          </Button>
        </Grid>
      </Grid>

      {/* Snackbar for showing success/error messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabTestMultiSelector;
